import { FAKE, REAL, registerDependency } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'

export const SPEED_ORDER_SEARCH_API = Symbol('SPEED_ORDER_SEARCH_API')

export interface ProductSearchResult {
  items: ProductItem[]
}

export interface ProductItem {
  id: number
  code: string
  name: string
  picture?: {
    small: string
  }
  sale_price_no_format?: number
  currency_symbol?: string
  order_default_qty?: number
  order_minimum_qty?: number
  default_uom?: string
  price_per_price_uom?: string | null
  price_uom?: string | null
  prices?: {
    sale: string
    currency_code: string
  }
  ordering?: {
    order_quantity: number
    minimum_order_quantity?: number
    default_uom?: string
  }
}

export class SpeedOrderSearchRequest {
  endpointUrl: URL
  method = 'GET' as const
  headers: HeadersInit = { Accept: 'application/json' }
  controller: AbortController = new AbortController()

  constructor(searchEndpoint: string, productCode: string) {
    // Create URL relative to current window location to prevent SSRF
    this.endpointUrl = new URL(searchEndpoint, window.location.href)
    // Add query parameter safely
    this.endpointUrl.searchParams.set('query', productCode)
  }
}

export interface SpeedOrderSearchAPI {
  searchByCode(request: SpeedOrderSearchRequest): Promise<ProductSearchResult | null>
}

// Fake product data for testing/Storybook
const fakeProductData: { [key: string]: ProductSearchResult } = {
  '1001': {
    items: [{
      id: 22,
      code: '1001',
      name: 'Eetkamerstoel houten poot rond',
      picture: {
        small: 'src/testing/assets/images/klapchair.jpg'
      },
      default_uom: 'Unit',
      prices: {
        sale: '100.0000',
        currency_code: 'EUR'
      },
      ordering: {
        order_quantity: 1,
        minimum_order_quantity: 1,
        default_uom: 'Unit'
      }
    }]
  },
  '1002': {
    items: [{
      id: 23,
      code: '1002',
      name: 'Eetkamerstoel houten poot vierkant',
      picture: {
        small: 'src/testing/assets/images/klapchair.jpg'
      },
      default_uom: 'Unit',
      prices: {
        sale: '121.0000',
        currency_code: 'EUR'
      },
      ordering: {
        order_quantity: 1,
        minimum_order_quantity: 1,
        default_uom: 'Unit'
      }
    }]
  },
  '1003': {
    items: [{
      id: 24,
      code: '1003',
      name: 'Eetkamerstoel metalen poot rond',
      picture: {
        small: 'src/testing/assets/images/klapchair.jpg'
      },
      default_uom: 'Unit',
      prices: {
        sale: '100.0000',
        currency_code: 'EUR'
      },
      ordering: {
        order_quantity: 1,
        minimum_order_quantity: 1,
        default_uom: 'Unit'
      }
    }]
  },
  '1004': {
    items: [{
      id: 25,
      code: '1004',
      name: 'Eetkamerstoel metalen poot vierkant',
      picture: {
        small: 'src/testing/assets/images/klapchair.jpg'
      },
      default_uom: 'Unit',
      prices: {
        sale: '100.0000',
        currency_code: 'EUR'
      },
      ordering: {
        order_quantity: 1,
        minimum_order_quantity: 1,
        default_uom: 'Unit'
      }
    }]
  },
  '1005': {
    items: [{
      id: 26,
      code: '1005',
      name: 'Eetkamerstoel kunststof poot rond',
      picture: {
        small: 'src/testing/assets/images/klapchair.jpg'
      },
      default_uom: 'Unit',
      prices: {
        sale: '85.0000',
        currency_code: 'EUR'
      },
      ordering: {
        order_quantity: 1,
        minimum_order_quantity: 1,
        default_uom: 'Unit'
      }
    }]
  },
  '1006': {
    items: [{
      id: 27,
      code: '1006',
      name: 'Zeskantbout ISO 4014 M10x120',
      picture: {
        small: 'src/testing/assets/images/klapchair.jpg'
      },
      default_uom: 'Stuk',
      price_per_price_uom: '€ 31,25',
      price_uom: 'Per 100 stuks',
      prices: {
        sale: '0.3125',
        currency_code: 'EUR'
      },
      ordering: {
        order_quantity: 50,
        minimum_order_quantity: 50,
        default_uom: 'Stuk'
      }
    }]
  }
}

@registerDependency(REAL, SPEED_ORDER_SEARCH_API)
export class SpeedOrderSearchService implements SpeedOrderSearchAPI {
  requests: SpeedOrderSearchRequest[] = []

  async searchByCode(request: SpeedOrderSearchRequest): Promise<ProductSearchResult | null> {
    this.requests.push(request)

    try {
      const response = await fetch(request.endpointUrl, {
        method: request.method,
        headers: request.headers,
        signal: request.controller.signal
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const apiResult = await response.json()

      // Handle different response formats
      let result: ProductSearchResult

      if (apiResult.items && Array.isArray(apiResult.items)) {
        // Standard format: { items: [...] }
        result = apiResult as ProductSearchResult
      } else if (apiResult.id && apiResult.code && apiResult.name) {
        // Single product format: wrap in items array
        result = { items: [apiResult as ProductItem] }
      } else {
        // Unknown format or no product found
        console.warn('Unexpected API response format:', apiResult)
        return null
      }

      return result
    } catch (error) {
      console.error('Product search failed:', error)
      return null
    }
  }
}

@registerDependency(FAKE, SPEED_ORDER_SEARCH_API)
export class FakeSpeedOrderSearchService implements SpeedOrderSearchAPI {
  requests: SpeedOrderSearchRequest[] = []
  response: ProductSearchResult | null | undefined = undefined

  async searchByCode(request: SpeedOrderSearchRequest): Promise<ProductSearchResult | null> {
    this.requests.push(request)

    // Extract product code from the request URL
    const productCode = request.endpointUrl.searchParams.get('query')

    if (!productCode?.trim()) {
      return null
    }

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 300))

    if (this.response !== undefined) {
      return this.response
    }

    const result = fakeProductData[productCode] || null
    return result
  }
}
